/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useEffect } from "react";
import PoAVerificationModal from "../../../../components/FormInfoModal/PoAVerificationModal";
import LayoutAuthenticated from "../../../Layouts/LayoutAuthenticated";
import { getProductData } from "@/src/services/productService";
import ApplicationForm from "../../../../components/ApplicationForm/ApplicationForm";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createResource, fetchCollection, fetchResource, queryKeys } from "@/src/services/reactQuery/reactQueryService";
import useProductParams from "../../../../hooks/useProductParams";
import LegalbirdAuthenticatedLoaderPage from "../../Loading/LegalbirdAuthenticatedLoaderPage";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";

const ProcessCostSupportFormPage = () => {
  const { productId } = useProductParams();
  const productApiUri = getProductData("divorce", "apiUri");
  const { data: divorce, isLoading } = useQuery({
    queryKey: queryKeys.item(productApiUri, productId),
    queryFn: () => fetchResource(productApiUri, productId),
  });

  return isLoading || !divorce ? <LegalbirdAuthenticatedLoaderPage /> : <ProcessCostSupportCollectionProvider divorce={divorce} />;
};

type ProcessCostSupportCollectionProviderProps = {
  divorce: AbstractCase;
};

const ProcessCostSupportCollectionProvider = ({ divorce }: ProcessCostSupportCollectionProviderProps) => {
  const { data: processCostSupportsOfCustomer = [], isLoading } = useQuery({
    queryKey: queryKeys.collection("/process_cost_supports"),

    queryFn: () => fetchCollection("/process_cost_supports"),
  });
  const queryClient = useQueryClient();

  const createPCSMutation = useMutation({
    mutationFn: createResource,
    onSuccess: (data) => queryClient.setQueryData(queryKeys.collection("/process_cost_supports"), [data]),
  });

  useEffect(() => {
    if (divorce && !isLoading && processCostSupportsOfCustomer.length === 0) {
      createPCSMutation.mutate({ uri: "/process_cost_supports", data: { divorce: "/divorces/" + divorce.id } });
    }
  }, [processCostSupportsOfCustomer]);

  return isLoading || processCostSupportsOfCustomer.length === 0 ? (
    <LegalbirdAuthenticatedLoaderPage />
  ) : (
    <ProcessCostSupportFormPageInner processCostSupportId={processCostSupportsOfCustomer[0].id} divorce={divorce} />
  );
};

type ProcessCostSupportFormPageInnerProps = {
  processCostSupportId: number;
  divorce: AbstractCase;
};

const ProcessCostSupportFormPageInner = ({ processCostSupportId, divorce }: ProcessCostSupportFormPageInnerProps) => {
  const { data: processCostSupport, isLoading } = useQuery({
    queryKey: queryKeys.item("/process_cost_supports", processCostSupportId),

    queryFn: () => fetchResource("/process_cost_supports", processCostSupportId),
  });

  if (isLoading || !processCostSupport) {
    return <LegalbirdAuthenticatedLoaderPage />;
  }

  return (
    <LayoutAuthenticated sidebar={false}>
      <ApplicationForm product={processCostSupport} />
      <PoAVerificationModal divorce={divorce} />
    </LayoutAuthenticated>
  );
};

export default ProcessCostSupportFormPage;
